<template>
  <!-- Always its own card, never inside the cart summary. The parent places it
       above or below the form, or below the summary card (compact). -->
  <card v-if="suggestions.length > 0" :style="{ 'background-color': bss.card_color }">
    <div class="row">
      <div class="col">
        <component :is="compact ? 'h4' : 'h3'" :style="{'font-family': bss.font_family_card, 'color': bss.text_color_card}" class="card-title mb-1">
          {{ bss.upsell_title || $t('book.upsell.title') }}
        </component>
        <p :style="{'font-family': bss.font_family_card, 'color': bss.text_color_card}" class="mb-3">
          {{ bss.upsell_subtext || $t('book.upsell.subtext') }}
        </p>
      </div>
    </div>
    <!-- One full-width row per event: details on the left, its nearest bookable times on the right -->
    <div v-for="s in suggestions" :key="'UPS' + s.game.game_id" class="upsell-row">
      <img v-if="imageFor(s.game)" class="upsell-img"
           :style="{'object-fit': imageFor(s.game).object_fit || 'cover'}" v-twin-src="imageFor(s.game).url"/>
      <div class="upsell-info">
        <p class="mb-0" :style="{'font-family': bss.font_family_card, 'color': bss.text_color_card}" style="font-size: medium">{{ s.game.game_name }}</p>
        <p class="mb-0" v-if="priceLabel(s)" :style="{'font-family': bss.font_family_card, 'color': bss.text_color_card}">{{ priceLabel(s) }}</p>
        <p class="mb-0 font-weight-bold" v-if="discountApplies(s)"
           :style="{'font-family': bss.font_family_card, 'color': bss.button_color}">{{ bss.upsell_badge_text }}</p>
      </div>
      <div class="upsell-times">
        <base-button v-for="slot in s.slots" :key="'UPT' + slot.id" simple size="sm" class="m-0"
                     :title="$t('book.upsell.bookNow')"
                     :style="{'font-family': bss.font_family_card, 'border-color': bss.button_color, 'color': bss.button_color}"
                     @click="$emit('select', { game: s.game, slot: slot })">
          {{ formatTime(slot.start_time) }}
        </base-button>
      </div>
    </div>
  </card>
</template>

<script>
// Checkout upsell strip: other events on the same booking site template with a
// bookable slot on the same day as the booking already in the cart. Candidates
// come from the schedules already loaded by the parent (one day, one template),
// classified by the shared slotDisplay util so the strip can never offer a slot
// the event selection step would not. The discount line is decided by the promo
// endpoint itself, asked in preview mode for every offered time, so the card
// only ever advertises what an apply at checkout would grant.
import axios from 'axios'
import { API_LOCATION } from '@/api-config'
import { time } from '@/plugins/dateFormatter'
import { getNow } from '@/util/serverTime'
import { parseGameImages, MOBILE_BREAKPOINT } from '@/util/gameImages'
import { normalizeSlots, getSlotType, isSlotBookable } from '@/util/slotDisplay'

const MAX_SUGGESTIONS = 3
const MAX_TIMES_PER_EVENT = 3
const MINUTES_PER_DAY = 1440

function toMinutes(t) {
  if (!t) return null
  const parts = String(t).split(':')
  const minutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
  return isNaN(minutes) ? null : minutes
}

// Query string of a preview call to the promo endpoint: would the linked code
// discount the cart if this time were booked? The line value is the event's
// lowest rate at the party size of the booking already in the cart, the same
// figure the card shows as the "from" price.
function probeQuery(bss, cartId, slot, game, entry, quantity) {
  const price = entry && entry.price !== null && entry.price !== undefined ? Number(entry.price) : 0
  const qty = Number(quantity) > 0 ? Math.round(Number(quantity)) : 1
  const perPerson = entry && Number(entry.pricingTypeId) === 1
  const raw = perPerson ? price * qty : price
  const value = Number(Math.round(Number(raw + 'e2')) + 'e-2')
  return {
    code: bss.upsell_promo_code,
    cart_id: cartId,
    preview: '1',
    probe_slot_id: slot.id,
    probe_game_id: game.game_id,
    probe_booking_date: String(slot.booking_date || '').slice(0, 10),
    // The feed carries microseconds (16:00:00.000000); the endpoint wants HH:MM:SS
    probe_start_time: String(slot.start_time || '').slice(0, 8),
    probe_value: value,
    probe_quantity: qty
  }
}

// One answer per cart and time, shared by every mounted card (the details and
// payment steps each mount one), so a time is asked once
const probeAnswers = {}

// Asks the promo endpoint once; any failure or odd answer reads as no
async function askEndpoint(url) {
  try {
    const response = await axios.get(url)
    const preview = response.data && response.data.preview
    return !!(preview && preview.applies === true)
  } catch (e) {
    return false
  }
}

function toQueryString(params) {
  return Object.keys(params).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k])).join('&')
}

export default {
  name: 'UpsellSuggestions',
  props: {
    games: { type: [Array, String], default: () => [] },
    cart: { type: Array, default: () => [] },
    bss: { type: Object, default: () => ({}) },
    locale: { type: [Object, String], default: () => ({}) },
    formatAmount: { type: Function, required: true },
    // The server cart the promo endpoint judges, and the venue code in its path
    cartId: { type: String, default: '' },
    companyCode: { type: String, default: '' },
    // False while the parent is still syncing the cart to the server: the promo
    // endpoint judges the server cart, so asking early judges a stale one
    cartSynced: { type: Boolean, default: true },
    // Narrow column next to the cart: heading matches the summary card's
    compact: { type: Boolean, default: false }
  },
  data() {
    // probeCache: per cart composition and offered time, whether the promo
    // endpoint approved the discount (true), refused it (false) or is still
    // being asked (null)
    return { priceCache: {}, probeCache: {} }
  },
  computed: {
    cartBookings() {
      return this.cart.filter(c => c && c.booking && c.booking.id && !c.item && !c.gift_card)
    },
    // The most recently added booking anchors the day and the "nearest" ordering
    anchor() {
      return this.cartBookings.length > 0 ? this.cartBookings[this.cartBookings.length - 1] : null
    },
    // Time ranges already taken by the cart, in minutes from midnight
    occupied() {
      const out = []
      for (const c of this.cartBookings) {
        const start = toMinutes(c.booking.start_time)
        let end = toMinutes(c.end_time || c.booking.end_time)
        if (end === null && start !== null && c.game && c.game.duration_minutes) end = start + Number(c.game.duration_minutes)
        if (end === null && start !== null) end = start + 1
        if (start !== null && end !== null && end < start) end += MINUTES_PER_DAY
        out.push({ date: c.booking.booking_date, start: start, end: end, allDay: Number(c.booking.is_all_day) === 1 })
      }
      return out
    },
    // The cart the promo endpoint judges: its answer is only good for this exact
    // set of bookings at these party sizes (the probe's line value follows the
    // party size), so a changed cart or a resubmitted player count asks again
    cartKey() {
      return this.cartId + '|' + this.cartBookings.map(c => c.booking.id + 'x' + (Number(c.quantity) || 0)).join(',')
    },
    suggestions() {
      if (Number(this.bss.show_upsell_section) !== 1) return []
      if (!this.anchor || !Array.isArray(this.games)) return []
      const anchorDate = this.anchor.booking.booking_date
      const anchorStart = toMinutes(this.anchor.booking.start_time)
      if (anchorStart === null) return []
      // An all day booking takes the whole day: nothing can sit beside it
      if (this.occupied.some(o => o.allDay && o.date === anchorDate)) return []
      const inCart = {}
      for (const c of this.cartBookings) inCart[c.game.game_id] = true
      const now = getNow()
      const picks = []
      for (const game of this.games) {
        if (!game || inCart[game.game_id] || !game.slots) continue
        let slots
        try { slots = normalizeSlots(game, this.bss, now) } catch (e) { continue }
        const candidates = []
        for (const slot of slots) {
          if (slot.booking_date !== anchorDate) continue
          if (Number(slot.is_all_day) === 1) continue
          const type = getSlotType(game, this.bss, slot, now)
          // A booking request must be alone in its cart, so it is never an add-on
          if (!isSlotBookable(type) || type === 'requestable') continue
          const start = toMinutes(slot.start_time)
          let end = toMinutes(slot.end_time)
          if (end === null && start !== null) end = start + Number(game.duration_minutes || 1)
          if (start === null || end === null) continue
          if (end < start) end += MINUTES_PER_DAY
          const overlaps = this.occupied.some(o => o.date === anchorDate && o.start !== null && o.end !== null && start < o.end && end > o.start)
          if (overlaps) continue
          candidates.push({ slot: slot, start: start, distance: Math.abs(start - anchorStart) })
        }
        if (candidates.length === 0) continue
        // The times nearest the booking, then shown in clock order
        candidates.sort((a, b) => a.distance - b.distance)
        const nearest = candidates.slice(0, MAX_TIMES_PER_EVENT)
        const distance = nearest[0].distance
        const priceSlot = nearest[0].slot
        nearest.sort((a, b) => a.start - b.start)
        picks.push({ game: game, slots: nearest.map(c => c.slot), priceSlot: priceSlot, distance: distance })
      }
      picks.sort((a, b) => a.distance - b.distance)
      return picks.slice(0, MAX_SUGGESTIONS)
    }
  },
  watch: {
    suggestions: {
      immediate: true,
      handler(list) {
        for (const s of list) {
          if (this.priceCache[s.game.game_id] === undefined) this.loadPrice(s)
          else this.probeRow(s)
        }
      }
    },
    // Once the server cart is current, ask about every row that was held back
    cartSynced(synced) {
      if (synced) for (const s of this.suggestions) this.probeRow(s)
    }
  },
  methods: {
    imageFor(game) {
      if (!game || !game.game_images) return null
      const imgs = parseGameImages(game.game_images, window.innerWidth < MOBILE_BREAKPOINT)
      return imgs && imgs.length > 0 ? imgs[0] : null
    },
    formatTime(t) {
      return time(this.locale.time_format, t)
    },
    // The discount line shows only when the promo endpoint approved every time
    // offered on the row for the cart as it stands, so the card never
    // advertises a discount that checkout would refuse.
    discountApplies(s) {
      if (!this.bss.upsell_promo_code || !this.bss.upsell_badge_text) return false
      return s.slots.every(slot => this.probeCache[this.probeKey(slot)] === true)
    },
    probeKey(slot) {
      return this.cartKey + ':' + slot.id
    },
    // Ask the promo endpoint about every time on the row that has not been
    // asked for this cart yet. Needs the row's price, which sets the line value.
    probeRow(s) {
      if (!this.bss.upsell_promo_code || !this.bss.upsell_badge_text) return
      if (!this.cartId || !this.companyCode || !this.cartSynced) return
      const entry = this.priceCache[s.game.game_id]
      if (!entry || entry.price === null) return
      for (const slot of s.slots) {
        const key = this.probeKey(slot)
        if (this.probeCache[key] !== undefined) continue
        this.$set(this.probeCache, key, null)
        this.probeSlot(key, s, slot, entry)
      }
    },
    async probeSlot(key, s, slot, entry) {
      if (!probeAnswers[key]) {
        const quantity = this.anchor ? this.anchor.quantity : 1
        const params = probeQuery(this.bss, this.cartId, slot, s.game, entry, quantity)
        const url = API_LOCATION + 'bookings/company-groups/' + encodeURIComponent(this.companyCode) + '/promos-v2?' + toQueryString(params)
        probeAnswers[key] = askEndpoint(url)
      }
      this.$set(this.probeCache, key, await probeAnswers[key])
    },
    priceLabel(s) {
      const entry = this.priceCache[s.game.game_id]
      if (!entry || entry.hidden || entry.price === null) return ''
      const key = Number(entry.pricingTypeId) === 1 ? 'book.upsell.fromPerPerson' : 'book.upsell.from'
      return this.$t(key, { price: this.formatAmount(entry.price) })
    },
    // Lowest rate for the slot, from the same pricing endpoint the ticket step uses
    async loadPrice(s) {
      this.$set(this.priceCache, s.game.game_id, { price: null, hidden: false, pricingTypeId: null })
      try {
        const url = API_LOCATION + 'games/' + s.game.game_id + '/pricing?booking_slot_id=' + s.priceSlot.id
        const response = await axios.get(url)
        const rows = response.data.pricing || []
        const settings = response.data.settings || {}
        let min = null
        for (const r of rows) {
          const p = parseFloat(r.price)
          if (!isNaN(p) && (min === null || p < min)) min = p
        }
        this.$set(this.priceCache, s.game.game_id, {
          price: min,
          hidden: Number(settings.hide_pricing_rate) === 1,
          pricingTypeId: settings.pricing_type_id
        })
      } catch (e) {
        this.$set(this.priceCache, s.game.game_id, { price: null, hidden: true, pricingTypeId: null })
      }
      this.probeRow(s)
    }
  }
}
</script>

<style scoped>
/* A row wraps on its own when the card is narrow (below the order summary,
   phones): the times drop under the event details. */
.upsell-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  padding: 12px 0;
}
.upsell-row + .upsell-row {
  border-top: 1px solid rgba(125, 125, 125, 0.25);
}
.upsell-info {
  flex: 1 1 160px;
  min-width: 0;
}
.upsell-times {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.upsell-img {
  border-radius: 50%;
  height: 56px;
  width: 56px;
  flex: 0 0 auto;
}
</style>
